import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { throttle } from 'lodash';
import { Adaptive } from '@custom-components';
import MobileNav from './MobileNav';
import DesktopNav from './DesktopNav';
import { NavWrapper, NavBorder } from './MainNav.styles';
// touch context
import { IsTouchEnabledContext } from './common/Context';

/**
 * This constant reflects the state nature of the navigation
 * The menu can either be in its normal state (page load);
 * In a forced hidden state (when we are scrolling down the page)
 * In a forced visible state (when we are scrolling up the page.)
 */
export const NAV_STATE = {
  STATIC: 'nav-static',
  SHOW: 'nav-visible',
  HIDE: 'nav-hidden'
};

export const linkPropType = PropTypes.shape({
  id: PropTypes.string,
  href: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
  target: PropTypes.string,
  onClick: PropTypes.func,
  icon: PropTypes.string,
  routeMatches: PropTypes.arrayOf(PropTypes.string)
});

export const subLinkPropType = PropTypes.shape({
  id: PropTypes.string,
  href: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
  target: PropTypes.string,
  onClick: PropTypes.func,
  icon: PropTypes.string,
  routeMatches: PropTypes.arrayOf(PropTypes.string),
  subMenu: PropTypes.arrayOf(linkPropType)
});

class MainNav extends Component {
  static propTypes = {
    /**
     * string of the telephone number for contacting lodgelink. only for mobile.
     */
    contactNumber: PropTypes.string,

    type: PropTypes.oneOf(['supplier', 'admin']).isRequired,

    isAdminToken: PropTypes.bool.isRequired,
    /**
     * Passed Component of the link that takes a user to the homepage.
     */
    logo: linkPropType,

    /**
     * Passed Component of the link that takes a user to the support page.
     */
    helpSupport: linkPropType,

    /**
     * Flagging whether user is logged in or not to determine wether to render user in nav with submenu or the passed joinLink.
     */
    authenticated: PropTypes.bool,

    /**
     * Passed component that
     */
    joinOrganization: linkPropType,

    /**
     * Authenticated Users First Name.
     */

    userFirstName: PropTypes.string,

    /**
     * Authenticated Users Last Name.
     */

    userLastName: PropTypes.string,

    /**
     * Authenticated Users Profile Pic (optional), defaults to users initials from First/Last Name.
     */

    userProfilePicture: PropTypes.string,

    /**
     * Authenticated Users Currently active organization id.
     */

    userCurrentOrganization: PropTypes.number,

    /**
     * List of Organizations the Authenticated user is associated to that when clicked sets current active organization
     */

    organizations: PropTypes.arrayOf(PropTypes.object),

    /**
     * Change handler to send back selected org so all places can be updated by parent/ancestor
     */
    organizationChange: PropTypes.func,

    /**
     * Link to sign out the current authenticated user.
     */
    signOut: linkPropType,

    /**
     * nav links in the user menu
     */
    userMenuLinks: PropTypes.arrayOf(linkPropType),

    /**
     * notification links
     */
    notifications: PropTypes.arrayOf(linkPropType),

    /**
     * notification links
     */
    subMenuLinks: PropTypes.arrayOf(subLinkPropType),

    /**
     * whether or not the side nav is expanded, only needs to be used in desktop/side nav
     */
    sideExpanded: PropTypes.bool,

    /**
     * function to pass down to change side expanded state
     */
    expandCollapseNav: PropTypes.func,

    /**
     * name of the route
     */
    routeName: PropTypes.string
  };

  static defaultProps = {
    contactNumber: null,
    authenticated: false,
    joinOrganization: null,
    logo: null,
    helpSupport: null,
    organizations: [],
    organizationChange: () => {},
    userCurrentOrganization: undefined,
    userFirstName: '',
    userLastName: '',
    userProfilePicture: null,
    signOut: null,
    subMenuLinks: [],
    userMenuLinks: [],
    notifications: [],
    sideExpanded: false,
    expandCollapseNav: () => [],
    routeName: 'Home'
  };

  constructor(props) {
    super(props);

    this.state = {
      isNavVisible: NAV_STATE.STATIC,
      isTouchDevice: false
    };

    // keep track of the scroll position
    this.lastScrollY = 0;
    // prevent infinite loop check var.
    this.touchCheckDone = false;
    // prevent nav update for mobile needs
    this.preventNavUpdate = false;

    this.handleScrolling = this.handleScrolling.bind(this);
    this.throttleScrolling = throttle(this.handleScrolling, 50);
  }

  componentDidMount() {
    if (window) window.addEventListener('scroll', this.throttleScrolling);

    if (!this.touchCheckDone) {
      const touchEnabled = this.testForTouch(window, document, navigator);
      const touchClass = touchEnabled ? 'touch' : 'no-touch';

      document.querySelector('.lodgelink-main-navigation').classList.add(touchClass);
    }
  }

  componentWillUnmount() {
    this.throttleScrolling.cancel();

    if (window) window.removeEventListener('scroll', this.throttleScrolling);
  }

  // set state we are on a touch device.
  testForTouch = (docWindow, docDocument, docNavigator) => {
    const isTouchDevice =
      !!(
        typeof docWindow !== 'undefined' &&
        ('ontouchstart' in docWindow ||
          (docWindow.DocumentTouch &&
            typeof docDocument !== 'undefined' &&
            docDocument instanceof docWindow.DocumentTouch))
      ) || !!(typeof docNavigator !== 'undefined' && (docNavigator.maxTouchPoints || docNavigator.msMaxTouchPoints));

    this.touchCheckDone = true;
    this.setState({ isTouchDevice });

    return isTouchDevice;
  };

  forceScrollCB = (yesNo) => {
    if (yesNo) {
      this.preventNavUpdate = true;
    } else {
      this.preventNavUpdate = false;
    }
  };

  handleScrolling = () => {
    const NAV_HEIGHT = 80;
    const NAV_HIDE_THRESHOLD = NAV_HEIGHT * 2;

    // figure out scroll direction and set nav state.
    const figureNavState = (oldY, newY) => {
      const { isNavVisible } = this.state;
      const passedNavThreshold = newY > NAV_HIDE_THRESHOLD;
      let newNavState = isNavVisible;

      if (newY > oldY) {
        // we are scrolling down
        newNavState = passedNavThreshold ? NAV_STATE.HIDE : NAV_STATE.STATIC;
      } else if (oldY > newY) {
        // we are scrolling up
        switch (passedNavThreshold) {
          case true:
            if (newNavState !== NAV_STATE.SHOW) newNavState = NAV_STATE.SHOW;
            break;
          case false:
            if (newNavState !== NAV_STATE.SHOW) newNavState = NAV_STATE.STATIC;
            break;
          default:
            break;
        }
      }

      // don't trigger out when we are not in show state
      if (isNavVisible === NAV_STATE.STATIC && newNavState === NAV_STATE.HIDE) {
        return {
          isNavVisible
        };
      }

      return {
        isNavVisible: newNavState
      };
    };

    const currentScrollY = window.scrollY || document.documentElement.scrollTop;
    const newState = figureNavState(this.lastScrollY, currentScrollY);
    this.lastScrollY = currentScrollY;

    if (!this.preventNavUpdate) {
      this.setState(newState);
    }
  };

  render() {
    const {
      userMenuLinks,
      userFirstName,
      userLastName,
      userCurrentOrganization,
      userProfilePicture,
      notifications,
      subMenuLinks,
      organizations,
      organizationChange,
      joinOrganization,
      contactNumber,
      logo,
      signOut,
      routeName,
      helpSupport,
      sideExpanded,
      expandCollapseNav,
      authenticated,
      type,
      isAdminToken
    } = this.props;
    const { isNavVisible, isTouchDevice } = this.state;

    return (
      <NavWrapper className={`${isNavVisible} lodgelink-main-navigation`}>
        <NavBorder className={isNavVisible === NAV_STATE.SHOW && 'mui-fixed'}>
          <IsTouchEnabledContext.Provider
            value={{
              isTouchDevice
            }}
          >
            <Adaptive
              sm={
                <MobileNav
                  type={type}
                  isAdminToken={isAdminToken}
                  authenticated={authenticated}
                  logo={logo}
                  helpSupport={helpSupport}
                  notifications={notifications}
                  userFirstName={userFirstName}
                  userLastName={userLastName}
                  userCurrentOrganization={userCurrentOrganization}
                  userProfilePicture={userProfilePicture}
                  organizations={organizations}
                  organizationChange={organizationChange}
                  joinOrganization={joinOrganization}
                  subMenuLinks={subMenuLinks}
                  signOut={signOut}
                  contactNumber={contactNumber}
                  userMenuLinks={userMenuLinks}
                  forceScrollCB={this.forceScrollCB}
                  routeName={routeName}
                />
              }
              lg={
                <DesktopNav
                  type={type}
                  isAdminToken={isAdminToken}
                  authenticated={authenticated}
                  logo={logo}
                  userFirstName={userFirstName}
                  userLastName={userLastName}
                  userCurrentOrganization={userCurrentOrganization}
                  userProfilePicture={userProfilePicture}
                  organizations={organizations}
                  organizationChange={organizationChange}
                  joinOrganization={joinOrganization}
                  signOut={signOut}
                  userMenuLinks={userMenuLinks}
                  notifications={notifications}
                  sideExpanded={sideExpanded}
                  expandCollapseNav={expandCollapseNav}
                  isTouchDevice={isTouchDevice}
                />
              }
            />
          </IsTouchEnabledContext.Provider>
        </NavBorder>
      </NavWrapper>
    );
  }
}

export default { MainNav };
export const IsTouchEnabledConsumer = IsTouchEnabledContext.Consumer;
